import { useMutation } from '@apollo/client'
import { useEffect, useState } from 'react'
import axios from 'axios'
import {
  Button,
  ButtonColor,
  NotificationType,
  VideoUpload,
} from '../../../../components'
import {
  GetCreatorIntroVideoUploadUrlResponse,
  GET_CREATOR_INTRO_VIDEO_UPLOAD_URL,
  UPDATE_CREATOR,
} from '../../../../gql'
import {
  Creator,
  MutationGetCreatorIntroVideoUploadUrlArgs,
} from '../../../../gql/types'
import { useNotification } from '../../../../utils/hooks'

interface Props {
  creator: Creator
  onContinue: () => void
  onBack: () => void
}

const ReapplyIntroVideo = ({ creator, onContinue, onBack }: Props) => {
  const { addNotification } = useNotification()

  const [updateCreator] = useMutation(UPDATE_CREATOR)
  const [getUploadUrl] = useMutation<
    GetCreatorIntroVideoUploadUrlResponse,
    MutationGetCreatorIntroVideoUploadUrlArgs
  >(GET_CREATOR_INTRO_VIDEO_UPLOAD_URL)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedFile, setSelectedFile] = useState(null)
  const [mime, setMime] = useState('')
  const [contentDimensions, setContentDimensions] = useState<{
    w: number | null
    h: number | null
  }>({ w: null, h: null })
  const [uploadProgress, setUploadProgress] = useState(0)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
      }
    }
  }, [previewUrl])

  const handleFileInput = (
    file: File,
    size?: {
      w: number | null
      h: number | null
    },
  ) => {
    if (!file) {
      setSelectedFile(null)
      setMime('')
      setPreviewUrl(null)
      return
    }

    setSelectedFile(file)
    setMime(file.type)
    setError(null)
    setContentDimensions(size)

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl)
    }
    const url = URL.createObjectURL(file)
    setPreviewUrl(url)
  }

  const onSubmit = async (event: React.MouseEvent) => {
    event.preventDefault()

    if (!selectedFile && creator?.introVideo?.url) {
      onContinue()
      return
    }

    if (!selectedFile) {
      setError('Please upload a video')
      return
    }

    setError(null)
    setLoading(true)
    setUploadProgress(0)

    try {
      const { data } = await getUploadUrl({
        variables: {
          creatorId: creator.id,
          mime,
        },
      })

      await axios.put(
        data.getCreatorIntroVideoUploadUrl.uploadUrl,
        selectedFile,
        {
          headers: {
            'Content-Type': mime,
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            )
            setUploadProgress(percentCompleted)
          },
        },
      )

      await updateCreator({
        variables: {
          id: creator.id,
          input: {
            introVideoFileName: data.getCreatorIntroVideoUploadUrl.fileName,
            introVideoWidth: contentDimensions.w,
            introVideoHeight: contentDimensions.h,
          },
        },
      })

      setSelectedFile(null)
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl)
        setPreviewUrl(null)
      }

      addNotification(
        'Intro video updated successfully',
        NotificationType.success,
      )
      onContinue()
    } catch (error) {
      console.log('@error ReapplyIntroVideo', error.message)
      addNotification(
        'Something went wrong. Please try again.',
        NotificationType.error,
      )
    }

    setLoading(false)
    setUploadProgress(0)
  }

  return (
    <div className='md:shadow rounded-t-md sm:overflow-hidden mb-8'>
      <div className='py-5 md:bg-white space-y-6 sm:p-6'>
        <div className='sm:mx-auto'>
          <h2 className='mt-0 md:text-center text-3xl font-extrabold text-gray-900'>
            Update Introduction Video
          </h2>
          <p className='mt-2 md:text-center text-sm text-gray-600'>
            Upload a new introduction video that better showcases your
            personality and content style.
          </p>
        </div>

        <div className=''>
          {loading && previewUrl && (
            <div className='flex flex-col justify-center rounded-md border-2 border-dashed border-gray-300 px-6 pt-5 pb-6 mt-4'>
              <div className='mb-4 flex mx-auto'>
                <video className='max-h-[350px]' src={previewUrl} controls />
              </div>
            </div>
          )}
          {!loading && (
            <VideoUpload
              onChange={handleFileInput}
              url={creator?.introVideo?.url ?? null}
            />
          )}
          {error && <p className='text-sm text-red-500'>{error}</p>}
        </div>

        <div className='mt-4 flex justify-between'>
          <Button
            title='Go back'
            type='button'
            colour={ButtonColor.white}
            disabled={loading}
            onClick={onBack}
          />
          <div className='py-2'>
            {!uploadProgress ? (
              <Button
                type='button'
                title='Continue'
                loading={loading}
                onClick={onSubmit}
                width='160px'
              />
            ) : (
              <Button
                type='button'
                title={`Uploading... ${uploadProgress}%`}
                loadingPercentage={uploadProgress}
                disabled={true}
                width='160px'
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export { ReapplyIntroVideo }
